import React, { Suspense } from 'react';
import { z } from 'zod/v3';
import { ok } from 'neverthrow';
import { Assert } from '@cotera/utilities';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import type { AskResponse } from '@cotera/client/v0/actions/framework/types';
import {
  ModalPageView,
  type ModalItem,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import type { AttachTriggerResult } from '@cotera/client/app/v1/trigger/new-trigger-wizard';
import {
  draftAddCronTrigger,
  draftAddDatasetTrigger,
  draftAddEventTrigger,
  draftAddWebhookTrigger,
  draftBindExistingTrigger,
} from './workflow-draft-graph';
import {
  workflowCanvasHasNodeSelection,
  workflowModelFromContext,
} from './workflow-toolbar-action.helpers';

const NewTriggerWizard = React.lazy(async () => {
  const module = await import(
    '@cotera/client/app/v1/trigger/new-trigger-wizard'
  );
  return { default: module.NewTriggerWizard };
});

type ModalManagerActions = {
  push: (item: ModalItem) => void;
  pop: () => void;
};

const ADD_TRIGGER_MODAL_SIZE = {
  custom: 'w-[92vw] max-w-2xl max-h-[85dvh] rounded-lg',
} as const;

const attachTriggerSchema = z.custom<AttachTriggerResult>();

export class WorkflowToolbarAddTriggerAction extends BaseAction<
  AttachTriggerResult,
  { t: string }[]
> {
  title = 'Add trigger';
  description = 'Add a trigger to this workflow';
  inputSchema = attachTriggerSchema;
  group = 'Workflow';
  icon = 'plus';

  constructor(private readonly modalManager: ModalManagerActions) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const model = workflowModelFromContext(context);
    if (model === null) {
      return false;
    }
    if (workflowCanvasHasNodeSelection(context)) {
      return false;
    }
    return true;
  }

  shouldAsk(_context: ApplicableContext): boolean {
    return true;
  }

  ask(
    _params: Partial<AttachTriggerResult>,
    _context: ExecuteContext
  ): AskResponse<AttachTriggerResult, { t: string }[]> {
    return {
      view: ({ onRespond, onCancel }) => {
        const handleCancel = async (): Promise<void> => {
          await onCancel();
          this.modalManager.pop();
        };
        this.modalManager.push({
          size: ADD_TRIGGER_MODAL_SIZE,
          view: () => (
            <ModalPageView className="flex-1 flex-col px-5 py-6 sm:px-10 sm:py-8">
              <Suspense
                fallback={
                  <div className="py-16 text-center text-sm text-muted-foreground">
                    Loading…
                  </div>
                }
              >
                <NewTriggerWizard
                  mode="attach"
                  onAttach={async (result) => {
                    await onRespond(result);
                    this.modalManager.pop();
                    return true;
                  }}
                  onCancel={handleCancel}
                />
              </Suspense>
            </ModalPageView>
          ),
          onClose: handleCancel,
        });
      },
    };
  }

  async execute(
    payload: AttachTriggerResult,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string }[]>> {
    const model = workflowModelFromContext(context);
    if (model === null) {
      return ok({});
    }
    switch (payload.type) {
      case 'existing':
        model.updateDraftState((state) =>
          draftBindExistingTrigger(
            state,
            model.getDraftContext(),
            payload.trigger
          )
        );
        break;
      case 'new-cron':
        model.updateDraftState((state) =>
          draftAddCronTrigger(
            state,
            model.getDraftContext(),
            payload.schedule,
            'UTC',
            payload.name
          )
        );
        break;
      case 'new-webhook': {
        const draftResult = draftAddWebhookTrigger(
          model.getDraftState(),
          model.getDraftContext(),
          {
            name: payload.draft.name,
            providerKey: payload.draft.providerKey,
            verificationConfig: payload.draft.verificationConfig,
            eventKey: payload.draft.eventKey ?? undefined,
            eventConfig: payload.draft.eventConfig,
          }
        );
        model.updateDraftState(() => draftResult.state);
        break;
      }
      case 'new-event':
        model.updateDraftState(
          (state) =>
            draftAddEventTrigger(
              state,
              model.getDraftContext(),
              payload.name,
              payload.eventName
            ).state
        );
        break;
      case 'new-dataset':
        model.updateDraftState(
          (state) =>
            draftAddDatasetTrigger(state, model.getDraftContext(), {
              name: payload.name,
              datasetId: payload.draft.datasetId,
              criterion: payload.draft.criterion,
              trackFields: payload.draft.trackFields,
              filter: payload.draft.filter,
              processExistingRows: payload.draft.processExistingRows,
              runMode: payload.draft.runMode,
            }).state
        );
        break;
      default:
        Assert.unreachable(payload);
    }
    return ok({});
  }
}
